import React, { useEffect, useRef, useState } from "react";
import { api } from "../contexts/AuthContext";
import logger from "../utils/logger";

import {
  service,
  factories,
  IEmbedConfiguration,
  Report,
} from "powerbi-client";
import { models } from "powerbi-client";
import Navbar from "./Navbar";

interface PowerBIEmbedProps {
  reportKey: string;
  workspaceId: string;
  title?: string;
}

interface EmbedConfigResponse {
  title: string;
  reportId: string;
  embedUrl: string;
  embedToken: string;
  expiration?: string;
}

const PowerBIEmbed: React.FC<PowerBIEmbedProps> = ({
  reportKey,
  workspaceId,
}) => {
  const title = useRef<string>("");
  const containerRef = useRef<HTMLDivElement | null>(null);
  const serviceRef = useRef<service.Service | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!serviceRef.current) {
      serviceRef.current = new service.Service(
        factories.hpmFactory,
        factories.wpmpFactory,
        factories.routerFactory,
      );
    }

    async function loadAndEmbed() {
      try {
        setLoading(true);
        setError(null);

        const resp = await api.get<EmbedConfigResponse>(
          `/powerbi/embed-config`,
          {
            params: { reportKey, workspaceId },
          },
        );

        if (cancelled) return;

        const {
          reportId,
          embedUrl,
          embedToken,
          title: reportTitle,
        } = resp.data;
        title.current = reportTitle;

        if (!containerRef.current || !serviceRef.current) {
          throw new Error("Power BI container not ready");
        }

        // Clear previous embeds
        serviceRef.current.reset(containerRef.current);

        const embedConfig: IEmbedConfiguration = {
          type: "report",
          id: reportId,
          embedUrl,
          accessToken: embedToken,
          tokenType: models.TokenType.Embed,
          permissions: models.Permissions.Read,
          settings: {
            panes: {
              filters: { visible: false },
              pageNavigation: { visible: false },
            },
            // Hide default error popups from the end user
            hideErrors: true,
          },
        };

        const report = serviceRef.current.embed(
          containerRef.current,
          embedConfig,
        ) as Report;

        report.off("loaded");
        report.on("loaded", () => {
          logger.info(`Power BI report [${reportKey}] loaded`);
        });

        report.off("rendered");
        report.on("rendered", () => {
          logger.info(`Power BI report [${reportKey}] rendered`);
        });

        report.off("error");
        report.on("error", (event: any) => {
          const errorDetails = event?.detail;
          logger.error("Power BI error:", errorDetails);
          // setError(errorDetails?.message || "Power BI rendering error");

          logger.error("Power BI Global Catch - Error Type:", errorDetails?.type);
          logger.error("Message:", errorDetails?.message);
          logger.error("Detailed Error:", errorDetails?.detailedMessage);

          // Differentiate between Fatal and Technical warnings
          if (errorDetails.type === "Fatal") {
            // Handle scenario where report completely fails to load
            setError(errorDetails?.message || "Power BI rendering error");
          }
        });
      } catch (err: any) {
        logger.error(err);
        if (!cancelled) {
          setError(err.message || "Failed to load Power BI report");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadAndEmbed();

    const currentContainer = containerRef.current;
    const currentService = serviceRef.current;

    return () => {
      cancelled = true;
      if (currentContainer && currentService) {
        currentService.reset(currentContainer);
      }
    };
  }, [reportKey, workspaceId]);

  return (
    <div className="powerbi-wrapper">
      <Navbar />
      {/* <div>{title && <h2 className="powerbi-title">&nbsp;{title.current}</h2>}</div> */}

      {loading && <div className="powerbi-loading">Loading dashboard…</div>}

      {error && (
        <div className="powerbi-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div
        ref={containerRef}
        className="powerbi-container"
        style={{ height: "100vh", width: "100%" }}
      />
    </div>
  );
};

export default PowerBIEmbed;
